/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback } from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormClearErrors,
  Control,
  UseFormWatch,
} from "react-hook-form";
import { FaSpinner, FaMapMarkerAlt, FaExternalLinkAlt } from "react-icons/fa";
import SelectInput from "../../../components/Select";
import Input from "../../../components/Input";
import {
  OrderFormData,
  ShortAddress,
  AddressOption,
  LocationState,
} from "../../../types/types";
import shortAddresses from "../../../riyadh_short_addresses.json";
import { useDistrictFetchData } from "../../../apis/locationApis";

interface ReceiverAddressProps {
  register: UseFormRegister<OrderFormData>;
  setValue: UseFormSetValue<OrderFormData>;
  watch: UseFormWatch<OrderFormData>;
  errors: FieldErrors<OrderFormData>;
  clearErrors: UseFormClearErrors<OrderFormData>;
  control: Control<OrderFormData>;
  className?: string;
  cities: any;
  handleCitySearch?: (query: string) => void;
  isCitiesFetchPending?: boolean;
  selectedOption: AddressOption;
  setSelectedOption: React.Dispatch<React.SetStateAction<AddressOption>>;
}

const ReceiverAddress: React.FC<ReceiverAddressProps> = ({
  register,
  errors,
  setValue,
  watch,
  control,
  clearErrors,
  className,
  cities,
  handleCitySearch,
  isCitiesFetchPending,
  selectedOption,
  setSelectedOption,
}) => {
  const [shortAddress, setShortAddress] = useState("");
  const [selectedAddress, setSelectedAddress] = useState<ShortAddress | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<ShortAddress[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [receiverLocation, setReceiverLocation] = useState<LocationState>({});
  const [pendingDistrictUpdate, setPendingDistrictUpdate] = useState<
    string | null
  >(null);
  const [searchRef, setSearchRef] = useState<string>("");
  const {
    data: districts = [],
    isPending: isDistrictFetchPending,
    refetch: fetchReceiverDistricts,
  } = useDistrictFetchData(
    receiverLocation?.city?.id,
    searchRef,
    selectedOption,
  );
 
  const handleDistrictChange = useCallback(
    (val: string) => {
      const district = districts.find((d: any) => d.id === val);
      if (!district) return;

      setValue("receiver.district", district.id);
      setValue("receiver.districtName", district.name);
      setReceiverLocation((prev) => ({ ...prev, district }));
    },
    [districts, setValue],
  );

  useEffect(() => {
    if (pendingDistrictUpdate && districts.length > 0) {
      const districtData = districts.find(
        (d: any) =>
          d.name.toLowerCase() === pendingDistrictUpdate.toLowerCase(),
      );

      if (districtData) {
        handleDistrictChange(districtData.id);
      }
      setPendingDistrictUpdate(null);
    }
  }, [districts, pendingDistrictUpdate, handleDistrictChange]);

  const handleDistrictSearch = (query: string) => {
    if (query.trim().length >= 3) {
      setSearchRef(query);
      fetchReceiverDistricts(); 
    }
  };


  const normalizeAddressData = useCallback((addr: any): ShortAddress => {
    const coordsStr = addr?.googleMapsLink?.split("/place/")[1];
    const coords = coordsStr?.split(",");

    const latitude = coords?.[0] ? parseFloat(coords[0]) : 0;
    const longitude = coords?.[1] ? parseFloat(coords[1]) : 0;

    return {
      shortAddress: addr.shortAddress,
      nationalAddress: {
        buildingNumber:
          addr.nationalAddress?.buildingNumber || addr.buildingNumber || "",
        street: addr.nationalAddress?.street || addr.street || "",
        secondaryNumber:
          addr.nationalAddress?.secondaryNumber || addr.secondaryNumber || "",
        district: addr.nationalAddress?.district || addr.district || "",
        postalCode: addr.nationalAddress?.postalCode || addr.postalCode || "",
        city: addr.nationalAddress?.city || addr.city || "",
      },
      location: addr.location || {
        latitude,
        longitude,
        googleMapsLink: addr.googleMapsLink || "",
      },
      googleMapsLink:
        addr.location?.googleMapsLink || addr.googleMapsLink || "",
    };
  }, []);


  const handleCityChange = useCallback(
    (val: string) => {
      const city = cities.find((c: any) => c.id === val);
      if (!city) return;

      setValue("receiver.city", city.id);
      setValue("receiver.cityName", city.name);
      setReceiverLocation((prev) => ({ ...prev, city, district: undefined }));
      setValue("receiver.district", "");

      if (city.id) {
        fetchReceiverDistricts();
      }
    },
    [cities, setValue, fetchReceiverDistricts],
  );


  const getAddressSuggestions = useCallback(
    (code: string) => {
      if (!code || code.length < 3) {
        setSuggestions([]);
        return;
      }

      const normalizedList = shortAddresses.map(normalizeAddressData);
      const filtered = normalizedList.filter((addr) =>
        addr.shortAddress.toLowerCase().includes(code.toLowerCase()),
      );

      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    },
    [normalizeAddressData],
  );

 
  const convertCityToTitleCase = useCallback((address: any) => {
    if (address.city && typeof address.city === "string") {
      address.city = address.city
        .toLowerCase()
        .split(" ")
        .filter(Boolean)
        .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }
    return address;
  }, []);


  const updateFormFields = useCallback(
    (addressData: ShortAddress) => {
      const address = convertCityToTitleCase(addressData);
      setSelectedAddress(address);
      setShortAddress(address.shortAddress);

      setValue(
        "receiver.buildingNumber",
        address.nationalAddress.buildingNumber,
      );
      setValue("receiver.streetName", address.nationalAddress.street);
      setValue("receiver.zipCode", address.nationalAddress.postalCode);

       
      if (address.location) {
        setValue("receiver.latitude", address.location.latitude.toString());
        setValue("receiver.longitude", address.location.longitude.toString());
      }

   
      const fullAddress = [
        address.nationalAddress.buildingNumber,
        address.nationalAddress.street,
        address.nationalAddress.district,
        address.nationalAddress.city,
        address.nationalAddress.postalCode,
        "Saudi Arabia",
      ]
        .filter(Boolean)
        .join(", ");

      setValue("receiver.address", fullAddress);
      setValue("receiver.nationalAddress", address?.shortAddress);

     
      if (address.nationalAddress.city) {
        const cityData = cities.find(
          (c: any) =>
            c.name.toLowerCase() === address.nationalAddress.city.toLowerCase(),
        );

        if (cityData) {
          handleCityChange(cityData.id);

          
          if (address.nationalAddress.district) {
            setPendingDistrictUpdate(address.nationalAddress.district);
          }
        }
      }

      setShowSuggestions(false);
    },
    [setValue, cities, convertCityToTitleCase, handleCityChange],
  );

  
  useEffect(() => {
    getAddressSuggestions(shortAddress);
  }, [shortAddress, getAddressSuggestions]);

  return (
    <div className={`bg-[#fdfdf6] rounded-md space-y-6 p-2 ${className}`}>
    
      <div className="space-y-3">
        <div className="flex flex-col gap-4"> 
          <label className="flex items-center gap-3 cursor-pointer">
            <div className="relative flex items-center">
              <input
                type="radio"
                name="addressOption"
                className="h-5 w-5 appearance-none rounded-full border border-slate-300 checked:border-[#FBD037] transition-all peer"
                checked={selectedOption === "short"}
                onChange={() => setSelectedOption("short")}
              />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full opacity-0 peer-checked:opacity-100 bg-[#FBD037] transition-opacity"></span>
            </div>
            <span className="text-sm font-medium">
              Short Address Code / National Address
            </span>
          </label>

      
          <label className="flex items-center gap-3 cursor-pointer">
            <div className="relative flex items-center">
              <input
                type="radio"
                name="addressOption"
                className="h-5 w-5 appearance-none rounded-full border border-slate-300 checked:border-[#FBD037] transition-all peer"
                checked={selectedOption === "manual"}
                onChange={() => setSelectedOption("manual")}
              />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full opacity-0 peer-checked:opacity-100 bg-[#FBD037] transition-opacity"></span>
            </div>
            <span className="text-sm font-medium">Manual Address Entry</span>
          </label>

          {/* Map Option */}
          {/* <label className="flex items-center gap-3 cursor-pointer">
            <div className="relative flex items-center">
              <input
                type="radio"
                name="addressOption"
                className="h-5 w-5 appearance-none rounded-full border border-slate-300 checked:border-[#FBD037] transition-all peer"
                checked={selectedOption === "map"}
                onChange={() => setSelectedOption("map")}
              />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full opacity-0 peer-checked:opacity-100 bg-[#FBD037] transition-opacity"></span>
            </div>
            <span className="text-sm font-medium">Fill From Google Maps</span>
          </label> */}
        </div>

        {selectedOption === "short" && (
          <div className="mt-4 space-y-2 relative">
            <div className="relative">
              <input
                className="w-full rounded-full py-[6px] px-4 pr-10 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E]/70"
                value={shortAddress}
                onChange={(e) => {
                  setShortAddress(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="Enter short address code"
              />
              {isLoading && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <FaSpinner className="animate-spin" />
                </div>
              )}
            </div>

            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="p-3 hover:bg-gray-50 cursor-pointer flex items-start"
                    onMouseDown={() => updateFormFields(suggestion)}
                  >
                    <FaMapMarkerAlt className="text-red-500 mt-1 mr-2 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-medium">
                        {suggestion.shortAddress}
                      </div>
                      <div className="text-sm text-gray-600">
                        {suggestion.nationalAddress.buildingNumber}{" "}
                        {suggestion.nationalAddress.street}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {suggestion.nationalAddress.district},{" "}
                        {suggestion.nationalAddress.city}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Selected Address Preview */}
            {selectedAddress && (
              <div className="p-3 bg-gray-50 rounded border border-gray-200">
                <div className="flex items-start gap-2">
                  <FaMapMarkerAlt className="text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <p className="text-sm font-medium">Resolved Address:</p>
                      {(selectedAddress.location?.googleMapsLink ||
                        selectedAddress.googleMapsLink) && (
                        <a
                          href={
                            selectedAddress.location?.googleMapsLink ||
                            selectedAddress.googleMapsLink
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 text-xs flex items-center"
                        >
                          View on Map <FaExternalLinkAlt className="ml-1" />
                        </a>
                      )}
                    </div>
                    <p className="text-sm mt-1">
                      {selectedAddress.nationalAddress.buildingNumber}{" "}
                      {selectedAddress.nationalAddress.street}
                      {selectedAddress.nationalAddress.secondaryNumber &&
                        ` (${selectedAddress.nationalAddress.secondaryNumber})`}
                    </p>
                    <div className="grid grid-cols-2 gap-1 mt-2 text-xs text-gray-500">
                      <div>
                        District: {selectedAddress.nationalAddress.district}
                      </div>
                      <div>City: {selectedAddress.nationalAddress.city}</div>
                      <div>
                        Postal Code:{" "}
                        {selectedAddress.nationalAddress.postalCode}
                      </div>
                      {selectedAddress.location && (
                        <div>
                          Coordinates:{" "}
                          {selectedAddress.location.latitude.toFixed(6)},{" "}
                          {selectedAddress.location.longitude.toFixed(6)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* No Results Message */}
            {shortAddress && suggestions.length === 0 && !isLoading && (
              <div className="text-sm text-gray-500 p-2 bg-gray-50 rounded">
                No matching addresses found for "{shortAddress}"
              </div>
            )}
          </div>
        )}

        {selectedOption === "manual" && (
          <div className="mt-4 grid grid-cols-4 gap-4">
            <SelectInput
              name="receiver.city"
              label="City"
              className="col-span-2"
              placeholder="Select City"
              selectOptions={cities.map((city: any) => ({
                id: city.id,
                value: city.id,
                text: city.name,
              }))}
              clearErrors={clearErrors}
              register={register}
              validation={{ required: "Receiver city is required" }}
              // disabled={!cities.length}
              value={receiverLocation?.city?.id}
              setValue={handleCityChange}
              onSearch={handleCitySearch}
              isPending={isCitiesFetchPending}
            />

            <SelectInput
              name="receiver.district"
              label="District"
              className="col-span-2"
              placeholder="Select District"
              selectOptions={districts.map((district: any) => ({
                id: district.id,
                value: district.id,
                text: district.name,
              }))}
              clearErrors={clearErrors}
              register={register}
              validation={{ required: "Receiver district is required" }}
              value={receiverLocation?.district?.id}
              setValue={handleDistrictChange}
              onSearch={handleDistrictSearch}
              isPending={isDistrictFetchPending}
            />

            <Input
              name="receiver.streetName"
              type="text"
              title="Street Name"
              register={register}
              error={errors.receiver?.streetName}
              validation={{ required: "Street Name is required" }}
              className="col-span-2"
            />

            <Input
              name="receiver.buildingNumber"
              type="text"
              title="Building Name / No"
              register={register}
              error={errors.receiver?.buildingNumber}
              validation={{ required: "Building Name / No is required" }}
              className="col-span-2"
            />

            <Input
              name="receiver.zipCode"
              type="text"
              title="Zip Code"
              register={register}
              error={errors.receiver?.zipCode}
              validation={{ required: "Zip code is required" }}
              className="col-span-2"
            />
          </div>
        )}

        {/* {selectedOption === "map" && (
          <OpenStreetMapInput
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
            clearErrors={clearErrors}
            control={control}
            cities={cities}
            districts={districts}
            receiverLocation={receiverLocation}
            setReceiverLocation={setReceiverLocation}
          />
        )} */}
      </div>
    </div>
  );
};

export default ReceiverAddress;
